// ./src/config/unitOptions.ts
// Unit options configuration for user preferences
// These options are used in selector components throughout the app

// ============================================
// Types
// ============================================

export interface UnitOption {
  /** Value stored in database */
  value: string;
  /** Translation key for full label - use with t('common:units.distance.km') */
  labelKey: string;
  /** Translation key for short label - use with t('common:units.distance.kmShort') */
  labelShortKey: string;
}

// ============================================
// Distance Units
// ============================================

export const DISTANCE_UNIT_OPTIONS: UnitOption[] = [
  { value: "km", labelKey: "units.distance.km", labelShortKey: "units.distance.kmShort" },
  { value: "mi", labelKey: "units.distance.mi", labelShortKey: "units.distance.miShort" },
];

export const DISTANCE_UNIT_MAP: Record<string, UnitOption> = Object.fromEntries(
  DISTANCE_UNIT_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Volume Units (for liquid fuels)
// ============================================

export const VOLUME_UNIT_OPTIONS: UnitOption[] = [
  { value: "l", labelKey: "units.volume.l", labelShortKey: "units.volume.lShort" },
  { value: "gal-us", labelKey: "units.volume.galUs", labelShortKey: "units.volume.galUsShort" },
  { value: "gal-uk", labelKey: "units.volume.galUk", labelShortKey: "units.volume.galUkShort" },
];

export const VOLUME_UNIT_MAP: Record<string, UnitOption> = Object.fromEntries(
  VOLUME_UNIT_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Tank Volume Units (includes kWh for electric)
// ============================================

export const TANK_VOLUME_UNIT_OPTIONS: UnitOption[] = [
  { value: "l", labelKey: "units.volume.l", labelShortKey: "units.volume.lShort" },
  { value: "gal-us", labelKey: "units.volume.galUs", labelShortKey: "units.volume.galUsShort" },
  { value: "gal-uk", labelKey: "units.volume.galUk", labelShortKey: "units.volume.galUkShort" },
  { value: "kwh", labelKey: "units.energy.kwh", labelShortKey: "units.energy.kwhShort" },
];

export const TANK_VOLUME_UNIT_MAP: Record<string, UnitOption> = Object.fromEntries(
  TANK_VOLUME_UNIT_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Consumption Units
// ============================================

export const CONSUMPTION_UNIT_OPTIONS: UnitOption[] = [
  { value: "l100km", labelKey: "units.consumption.l100km", labelShortKey: "units.consumption.l100kmShort" },
  { value: "km-l", labelKey: "units.consumption.kmL", labelShortKey: "units.consumption.kmLShort" },
  { value: "mpg-us", labelKey: "units.consumption.mpgUs", labelShortKey: "units.consumption.mpgUsShort" },
  { value: "mpg-uk", labelKey: "units.consumption.mpgUk", labelShortKey: "units.consumption.mpgUkShort" },
  { value: "mi-l", labelKey: "units.consumption.miL", labelShortKey: "units.consumption.miLShort" },
  { value: "kwh100km", labelKey: "units.consumption.kwh100km", labelShortKey: "units.consumption.kwh100kmShort" },
];

export const CONSUMPTION_UNIT_MAP: Record<string, UnitOption> = Object.fromEntries(
  CONSUMPTION_UNIT_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Temperature Units
// ============================================

export const TEMPERATURE_UNIT_OPTIONS: UnitOption[] = [
  { value: "C", labelKey: "units.temperature.c", labelShortKey: "units.temperature.cShort" },
  { value: "F", labelKey: "units.temperature.f", labelShortKey: "units.temperature.fShort" },
];

export const TEMPERATURE_UNIT_MAP: Record<string, UnitOption> = Object.fromEntries(
  TEMPERATURE_UNIT_OPTIONS.map((opt) => [opt.value, opt])
);

// ============================================
// Helper Functions
// ============================================

export const getDistanceUnitOption = (value: string | null | undefined): UnitOption | undefined => {
  if (!value) return undefined;
  return DISTANCE_UNIT_MAP[value];
};

export const getVolumeUnitOption = (value: string | null | undefined): UnitOption | undefined => {
  if (!value) return undefined;
  return VOLUME_UNIT_MAP[value];
};

export const getTankVolumeUnitOption = (value: string | null | undefined): UnitOption | undefined => {
  if (!value) return undefined;
  return TANK_VOLUME_UNIT_MAP[value];
};

export const getConsumptionUnitOption = (value: string | null | undefined): UnitOption | undefined => {
  if (!value) return undefined;
  return CONSUMPTION_UNIT_MAP[value];
};

export const getTemperatureUnitOption = (value: string | null | undefined): UnitOption | undefined => {
  if (!value) return undefined;
  return TEMPERATURE_UNIT_MAP[value];
};